import { useState, useEffect, useRef } from 'react';
import CustomStore from 'devextreme/data/custom_store';
import { useInfiniteQuery } from '@tanstack/react-query';
import { LoadOptions } from 'devextreme/data';
import useHandleScrollBottomEvent from './useHandleScrollBottomEvent';
import { NewColumn, PagingOptions, PagingOptionsActive } from '../datagrid-types';
import { calculatePageNumber, combineFilterStrings, generateFilterString, generateSortString } from './helpers';
import { useSettings } from 'src/@core/hooks/useSettings';

/**
 * A hook that returns a data source for a grid component.
 *
 * @param {Object} options - The options object.
 * @param {PagingOptions} [options.pagingOptions] - The paging options.
 * @param {any[]} [options.datasource] - The data source array.
 * @param {CustomStore} [options.customStore] - The custom store.
 * @param {number} options.pageSize - The page size.
 * @param {string} [options.name=''] - The name of the data source.
 * @param {Function} [options.onScrollBottom] - The function to call when the grid is scrolled to the bottom.
 * @returns {any[] | CustomStore<any, any>} The data source.
 */
const useGridDataSource = ({
  pagingOptions,
  datasource,
  customStore,
  pageSize,
  name = '',
  columns,
  onScrollBottom,
  ignoreDates = false
}: {
  pagingOptions?: PagingOptions;
  datasource?: any[];
  customStore?: CustomStore;
  pageSize: number;
  name?: string;
  columns: NewColumn[];
  onScrollBottom?: () => void;
  ignoreDates?: boolean;
}) => {
  const [source, setSource] = useState<any[] | CustomStore<any, any>>([]);

  const pagedStore = useInfinitePagingStore(pagingOptions, pageSize, name, onScrollBottom);
  const standardPagedStore = useStandardPagingStore(pageSize, pagingOptions ?? { enable: false }, columns, ignoreDates);

  useEffect(() => {
    const store = pagingOptions?.enable
      ? 'scrollMode' in pagingOptions && pagingOptions?.scrollMode === 'standard'
        ? standardPagedStore
        : pagedStore
      : [];

    if (pagingOptions?.enable) {
      setSource(store ?? []);
    } else if (customStore) {
      setSource(customStore);
    } else if (datasource) {
      setSource(datasource);
    } else {
      setSource([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStore, datasource, pagingOptions?.enable, pagedStore, standardPagedStore]);

  return source;
};

/**
 * A custom hook that returns a `CustomStore` instance for use with the DevExtreme Grid component.
 * - Note: this only works when the scrollMode is infinite and remote operations is disabled.
 *
 * @param pagingOptions - The paging options to use for the query.
 * @param pageSize - The number of items to fetch per page.
 * @param name - The name of the grid.
 * @param onScrollBottom - A callback function to be called when the user scrolls to the bottom of the grid.
 * @returns A `CustomStore` instance.
 */
const useInfinitePagingStore = (
  pagingOptions: PagingOptions | undefined,
  pageSize: number,
  name: string,
  onScrollBottom?: () => void
) => {
  const [customStore, setCustomStore] = useState<CustomStore | null>(null);

  const storeRef = useRef<CustomStore<any, any> | null>(null);
  const dataRef = useRef<any>({});

  const filterString = pagingOptions?.enable && 'filterString' in pagingOptions ? pagingOptions?.filterString : '';
  const sortString = pagingOptions?.enable && 'sortString' in pagingOptions ? pagingOptions?.sortString : '';
  const showDeleted = pagingOptions?.enable && 'showDeleted' in pagingOptions ? pagingOptions?.showDeleted : false;

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [`${name}-grid-infinite-paging-query`, pageSize, filterString, sortString, showDeleted, pagingOptions],
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        pageSize: pageSize,
        pageNumber: pageParam,
        includeMetadata: true,
        filter: filterString,
        sort: sortString,
        showDeleted: showDeleted
      };

      const res = await (pagingOptions as PagingOptionsActive)?.queryFunction?.(params);

      return res.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      //console.log({ lastPage, allPages, result: lastPage.items!.length < pageSize ? undefined : allPages.length });

      return lastPage.items!.length < pageSize ? undefined : allPages.length;
    },
    enabled: pagingOptions?.enable && (pagingOptions as PagingOptionsActive)?.scrollMode === 'infinite',
    refetchOnWindowFocus: false
  });

  dataRef.current = data;

  /**
   * Options for configuring the data store used by the grid.
   */
  const storeOptions = {
    key: 'id',
    load: (_options: LoadOptions) => {
      const { pages = [] } = dataRef.current ?? {};
      const { totalItemCount = 0 } = pages?.[0] ?? {};

      return Promise.resolve({
        data: pages?.reduce((acc: any, curr: any) => acc.concat(curr.items), []),
        totalCount: totalItemCount
      });
    }
  };

  const hasNextPageRef = useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;

  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  isFetchingNextPageRef.current = isFetchingNextPage;

  /**
   * Scroll bottom event handler.
   * When the user scrolls to the bottom of the grid, it fetches the next page of data.
   */
  useHandleScrollBottomEvent(() => {
    //console.count('bottom');
    //console.log({ hasNextPage, isFetchingNextPage });
    if (hasNextPageRef?.current && !isFetchingNextPageRef.current) fetchNextPage();
    if (typeof onScrollBottom === 'function') onScrollBottom();
  });

  /**
   * Returns the CustomStore instance for the grid data source. If the store doesn't exist, it creates a new one.
   * @returns {CustomStore} The CustomStore instance for the grid data source.
   */
  const getStore = () => {
    if (!storeRef.current) {
      storeRef.current = new CustomStore(storeOptions);
    }

    return storeRef.current;
  };

  useEffect(() => {
    if (pagingOptions?.enable) {
      setCustomStore(getStore());
    } else {
      setCustomStore(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagingOptions?.enable]);

  return customStore;
};

/**
 * Returns a CustomStore instance for the grid data source with standard paging options.
 * @param {number} pageSize - The number of items to display per page.
 * @param {PagingOptions} pagingOptions - The paging options to use for the data source.
 * @returns {CustomStore | null} The CustomStore instance for the grid data source.
 */
const useStandardPagingStore = (
  pageSize = 50,
  pagingOptions: PagingOptions,
  columns: NewColumn[],
  ignoreDates?: boolean
) => {
  const [customStore, setCustomStore] = useState<CustomStore | null>(null);

  const storeRef = useRef<CustomStore<any, any> | null>(null);
  const totalCountRef = useRef<number>(pageSize);
  const { settings } = useSettings();

  /**
   * Options for configuring the data store used by the grid.
   */
  const storeOptions = {
    key: 'id',
    load: async (options: LoadOptions) => {
      const page = calculatePageNumber(options, totalCountRef.current, pageSize);
      const additionalFilters = generateFilterString(options, columns.filter(col => col?.visible !== false) as any, ignoreDates ?? false);
      const sortString = generateSortString(options);

      let filter = pagingOptions?.enable && 'filterString' in pagingOptions ? pagingOptions?.filterString : '';
      const showDeleted = pagingOptions?.enable && 'showDeleted' in pagingOptions ? pagingOptions?.showDeleted : false;

      if (additionalFilters) {
        filter = combineFilterStrings(filter, additionalFilters);
      }

      if (filter != '') {
        filter = filter + ' and ' + 'isUATConsignment eq ' + settings.showUATRecords;
      } else {
        filter = 'isUATConsignment eq ' + settings.showUATRecords;
      }

      const params = {
        pageSize: pageSize,
        pageNumber: page,
        includeMetadata: true,
        filter: filter,
        sort: sortString,
        showDeleted: showDeleted
      };

      const res = await (pagingOptions as PagingOptionsActive)?.queryFunction?.(params);
      const { data } = res;

      totalCountRef.current = data?.totalItemCount ?? 0;

      return Promise.resolve({
        data: data.items ?? [],
        totalCount: data?.totalItemCount ?? 0
      });
    }
  };

  /**
   * Returns the CustomStore instance for the grid data source. If the store doesn't exist, it creates a new one.
   * @returns {CustomStore} The CustomStore instance for the grid data source.
   */
  const getStore = () => {
    if (!storeRef.current) {
      storeRef.current = new CustomStore(storeOptions);
    }

    return storeRef.current;
  };

  useEffect(() => {
    if (pagingOptions?.enable) {
      setCustomStore(getStore());
    } else {
      setCustomStore(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagingOptions?.enable]);

  return customStore;
};

export default useGridDataSource;
